// API service for backend communication with extensive logging
import { API_CONFIG } from '../config';
import type { CanonicalTopicOption } from '../constants/topics';
import { redactApplicationMessages } from '../utils/contentSafety';

// Base API URL - uses smart environment-based configuration
const API_URL = API_CONFIG.baseUrl;
const BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

// Auth token for Bearer authentication (set by Auth0UserContext)
// Hydrate from localStorage so tokens survive reload (email OTP flow has no
// Auth0 SDK session to fall back on).
const PERSISTED_TOKEN_KEY = 'gs_access_token';

const isUsableAccessToken = (token: string | null): token is string => {
  if (!token) return false;
  const parts = token.split('.');
  // Some identity providers may issue opaque access tokens. They are still a
  // credential; a backend 401 remains the source of truth for revocation.
  if (parts.length !== 3) return true;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded));
    // Avoid starting authenticated polling with an expired (or imminently
    // expiring) JWT restored from a previous browser session.
    return typeof payload.exp !== 'number' || payload.exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
};

let _authToken: string | null = (() => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const stored = localStorage.getItem(PERSISTED_TOKEN_KEY);
    if (isUsableAccessToken(stored)) return stored;
    localStorage.removeItem(PERSISTED_TOKEN_KEY);
    return null;
  } catch {
    return null;
  }
})();

export const setAuthToken = (token: string | null) => {
  _authToken = isUsableAccessToken(token) ? token : null;
  try {
    if (typeof localStorage === 'undefined') return;
    if (_authToken) {
      localStorage.setItem(PERSISTED_TOKEN_KEY, _authToken);
    } else {
      localStorage.removeItem(PERSISTED_TOKEN_KEY);
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — in-memory only
  }
};

// Fallback: read Auth0's cached access_token directly from localStorage.
// Auth0UserContext sets _authToken asynchronously, so early component
// useEffects can fire before it lands. Without this fallback, those
// requests go out unauthenticated and get 401'd.
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || '';
const getAuthTokenFromStorage = (): string | null => {
  if (typeof localStorage === 'undefined') return null;
  // 1. Auth0 SPA SDK cache (social login)
  if (AUTH0_CLIENT_ID) {
    try {
      const prefix = `@@auth0spajs@@::${AUTH0_CLIENT_ID}::`;
      // Prefer the entry whose audience matches our API, not @@user@@
      let fallback: string | null = null;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix) || key.endsWith('@@user@@')) continue;
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const entry = JSON.parse(raw);
        const tok: string | undefined = entry?.body?.access_token;
        const expiresAt: number | undefined = entry?.expiresAt;
        if (!tok) continue;
        if (expiresAt && expiresAt * 1000 < Date.now()) continue;
        if (!isUsableAccessToken(tok)) continue;
        // Only return tokens with a kid header (RS256 API tokens). Opaque or
        // HS256 tokens fail backend validation.
        const parts = tok.split('.');
        if (parts.length !== 3) continue;
        let hdr: any;
        try {
          hdr = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
        } catch {
          continue;
        }
        if (!hdr?.kid) continue;
        // Prefer audience match; otherwise remember as fallback
        if (AUTH0_AUDIENCE && key.indexOf(encodeURIComponent(AUTH0_AUDIENCE)) !== -1) {
          return tok;
        }
        if (!fallback) fallback = tok;
      }
      if (fallback) return fallback;
    } catch {
      // fall through to persisted-key check
    }
  }
  // 2. Persisted token written by setAuthToken (email OTP flow)
  try {
    const persisted = localStorage.getItem(PERSISTED_TOKEN_KEY);
    if (isUsableAccessToken(persisted)) return persisted;
    localStorage.removeItem(PERSISTED_TOKEN_KEY);
    return null;
  } catch {
    return null;
  }
};

const resolveAuthToken = (): string | null => {
  if (isUsableAccessToken(_authToken)) return _authToken;
  if (_authToken) setAuthToken(null);
  return getAuthTokenFromStorage();
};

export const HEADER_AUTH_USER_KEY = 'headerAuthUser';

/** Whether the current browser session can make an authenticated API call. */
export const hasCredentialReadyAuth = (): boolean => {
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;
  if (sessionStorage.getItem('botLoginActive') === 'true') {
    return Boolean(userId && sessionStorage.getItem('botAuthSecret'));
  }
  if (sessionStorage.getItem('testLoginActive') === 'true') {
    return Boolean(userId && sessionStorage.getItem('qaAuthSecret'));
  }
  return Boolean(resolveAuthToken());
};

// Build auth headers for direct fetch calls (outside fetchAPI)
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const isBotLogin = sessionStorage.getItem('botLoginActive') === 'true';
  const isTestLogin = sessionStorage.getItem('testLoginActive') === 'true';
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;
  const isBotUser = currentUser?.is_bot === true;
  const token = resolveAuthToken();
  if (isBotLogin && userId) {
    headers['X-Bot-User-ID'] = userId.toString();
    const botSecret = sessionStorage.getItem('botAuthSecret');
    if (botSecret) {
      headers['X-Bot-Auth-Secret'] = botSecret;
    }
    return headers;
  }
  // QA test login mode: always use X-User-ID, skip Bearer token
  if (isTestLogin && userId && !isBotUser) {
    headers['X-User-ID'] = userId.toString();
    const qaSecret = sessionStorage.getItem('qaAuthSecret');
    if (qaSecret) {
      headers['X-QA-Secret'] = qaSecret;
    }
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else if (userId && !isBotUser) {
    headers['X-User-ID'] = userId.toString();
    const qaSecret = sessionStorage.getItem('qaAuthSecret');
    if (qaSecret) {
      headers['X-QA-Secret'] = qaSecret;
    }
  }
  return headers;
};

// Debug logging configuration - DISABLED IN PRODUCTION
const IS_DEV = process.env.NODE_ENV !== 'production';
const DEBUG = {
  enabled: IS_DEV,           // Master switch - only enable in development
  requests: IS_DEV,          // Log request details for debugging
  responses: IS_DEV,         // Log response details for debugging
  returnValues: IS_DEV,      // Log final return values for debugging
  errors: true,              // Always log errors (but not full payloads in prod)
  timing: IS_DEV,            // Log timing information
  truncateResponses: true,   // Truncate large response payloads in logs
  maxResponseLength: 500,    // Max length for response logging
  logHeaders: IS_DEV,        // Log HTTP headers (dev only - may contain auth tokens)
  logPayloads: IS_DEV,       // Log full payloads (dev only - may contain sensitive data)
  logUserContext: IS_DEV     // Log user context (dev only)
};

// Request counter for logging correlation
let requestCounter = 0;

// Session ID for request correlation across tiers
const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;

// Log levels for structured logging
enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL'
}

// Alert priorities for ops
enum AlertPriority {
  P0_CRITICAL = 'P0_CRITICAL',    // System down, data loss
  P1_HIGH = 'P1_HIGH',            // Major feature broken
  P2_MEDIUM = 'P2_MEDIUM',        // Minor issues
  P3_LOW = 'P3_LOW'               // Informational
}

// Cache configuration
const CACHE_CONFIG = {
  enabled: true,
  ttl: 10 * 60 * 1000, // 10 minutes (increased from 5 for better dev performance)
  keys: {
    shoots: 'cached_shoots',
    requests: 'cached_requests',
    userShoots: 'cached_user_shoots',
    userRequests: 'cached_user_requests'
  }
};

// Simple cache implementation
class SimpleCache {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();

  set(key: string, data: any) {
    if (!CACHE_CONFIG.enabled) return;
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  get(key: string) {
    if (!CACHE_CONFIG.enabled) return null;

    const cached = this.cache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > CACHE_CONFIG.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  clear() {
    this.cache.clear();
  }

  clearByKey(key: string) {
    this.cache.delete(key);
  }

  clearByPrefix(prefix: string) {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

const apiCache = new SimpleCache();

// In-flight request deduplication map (prevents duplicate concurrent GETs)
const inflightRequests = new Map<string, Promise<any>>();

// Logging utility
const logger = {
  request: (requestId: number, method: string, endpoint: string, data?: any, headers?: any) => {
    if (!DEBUG.enabled || !DEBUG.requests) return;

    const timestamp = new Date().toISOString();
    const logData = {
      level: LogLevel.INFO,
      requestId,
      sessionId: SESSION_ID,
      timestamp,
      method,
      endpoint,
      fullUrl: `${API_URL}${endpoint}`,
      payload: DEBUG.logPayloads ? data : '[PAYLOAD_LOGGING_DISABLED]',
      headers: DEBUG.logHeaders ? headers : '[HEADER_LOGGING_DISABLED]',
      userContext: DEBUG.logUserContext ? {
        userId: localStorage.getItem('userId'),
        username: localStorage.getItem('username')
      } : null
    };

    console.group(`🚀 API Request #${requestId} [${timestamp}] ${method} ${endpoint}`);
    console.log('📊 STRUCTURED LOG:', JSON.stringify(logData, null, 2));
    console.groupEnd();
  },

  response: (requestId: number, endpoint: string, status: number, data: any, timeTaken: number, headers?: any) => {
    if (!DEBUG.enabled || !DEBUG.responses) return;

    const timestamp = new Date().toISOString();
    const responseSize = JSON.stringify(data).length;
    const logData = {
      level: LogLevel.INFO,
      requestId,
      sessionId: SESSION_ID,
      timestamp,
      endpoint,
      status,
      timeTaken,
      responseSize,
      headers: DEBUG.logHeaders ? headers : '[HEADER_LOGGING_DISABLED]',
      data: DEBUG.logPayloads ? data : '[PAYLOAD_LOGGING_DISABLED]'
    };

    console.group(`✅ API Response #${requestId} [${timestamp}] ${endpoint} (${status}) - ${timeTaken}ms`);
    console.log('📊 STRUCTURED LOG:', JSON.stringify(logData, null, 2));

    if (data && DEBUG.logPayloads) {
      if (DEBUG.truncateResponses && responseSize > DEBUG.maxResponseLength) {
        console.log('Response (truncated):', JSON.stringify(data).substring(0, DEBUG.maxResponseLength) + '...');
        console.log('Full response length:', responseSize);
      } else {
        console.log('Response:', data);
      }
    }
    console.groupEnd();
  },

  returnValue: (requestId: number, endpoint: string, functionName: string, value: any) => {
    if (!DEBUG.enabled || !DEBUG.returnValues) return;

    const timestamp = new Date().toISOString();
    console.group(`📦 Return Value #${requestId} [${timestamp}] ${functionName} (${endpoint})`);
    console.log('Value returned to caller:', value);
    console.groupEnd();
  },

  error: (requestId: number, endpoint: string, error: any, timeTaken: number, priority: AlertPriority = AlertPriority.P2_MEDIUM) => {
    if (!DEBUG.enabled || !DEBUG.errors) return;

    const timestamp = new Date().toISOString();
    const logData = {
      level: LogLevel.ERROR,
      alertPriority: priority,
      requestId,
      sessionId: SESSION_ID,
      timestamp,
      endpoint,
      timeTaken,
      error: {
        message: error.message || String(error),
        name: error.name,
        stack: error.stack,
        status: error.status,
        statusText: error.statusText
      },
      userContext: DEBUG.logUserContext ? {
        userId: localStorage.getItem('userId'),
        username: localStorage.getItem('username')
      } : null
    };

    console.group(`❌ API Error #${requestId} [${timestamp}] ${endpoint} - ${timeTaken}ms [${priority}]`);
    console.error('📊 STRUCTURED LOG:', JSON.stringify(logData, null, 2));
    console.error('Error:', error);
    if (error.stack) {
      console.error('Stack Trace:', error.stack);
    }
    console.groupEnd();

    // Send alert email for P0/P1 errors
    if (priority === AlertPriority.P0_CRITICAL || priority === AlertPriority.P1_HIGH) {
      sendAlertEmail(logData);
    }
  },

  cache: (action: 'hit' | 'miss' | 'set', key: string) => {
    if (!DEBUG.enabled) return;
    const emoji = action === 'hit' ? '✅' : action === 'miss' ? '❌' : '💾';
    console.log(`${emoji} Cache ${action}: ${key}`);
  },

  // Lightweight logging for in-flight request deduplication
  inflight: (action: 'start' | 'join' | 'end', key: string) => {
    if (!DEBUG.enabled) return;
    const label = action === 'start' ? '🚦 In-flight start' : action === 'join' ? '🔁 In-flight join' : '🏁 In-flight end';
    console.log(`${label}: ${key}`);
  },

  // State transition logging for multi-step operations
  stateTransition: (requestId: number, operation: string, fromState: string, toState: string, metadata?: any) => {
    if (!DEBUG.enabled) return;
    const timestamp = new Date().toISOString();
    const logData = {
      level: LogLevel.INFO,
      requestId,
      sessionId: SESSION_ID,
      timestamp,
      operation,
      stateTransition: `${fromState} → ${toState}`,
      metadata: metadata || {}
    };
    console.log(`🔄 State Transition #${requestId}: ${operation} [${fromState} → ${toState}]`);
    console.log('📊 STRUCTURED LOG:', JSON.stringify(logData, null, 2));
  },

  // Performance breakdown logging
  performanceBreakdown: (requestId: number, operation: string, breakdown: Record<string, number>) => {
    if (!DEBUG.enabled || !DEBUG.timing) return;
    const timestamp = new Date().toISOString();
    const total = Object.values(breakdown).reduce((sum, time) => sum + time, 0);
    const logData = {
      level: LogLevel.DEBUG,
      requestId,
      sessionId: SESSION_ID,
      timestamp,
      operation,
      totalTime: total,
      breakdown,
      percentages: Object.entries(breakdown).reduce((acc, [key, value]) => {
        acc[key] = `${((value / total) * 100).toFixed(2)}%`;
        return acc;
      }, {} as Record<string, string>)
    };
    console.log(`⚡ Performance Breakdown #${requestId}: ${operation} (Total: ${total}ms)`);
    console.log('📊 STRUCTURED LOG:', JSON.stringify(logData, null, 2));
  }
};

// Alert email system for critical errors
async function sendAlertEmail(logData: any): Promise<void> {
  try {
    // Send alert to backend endpoint which will email ops team
    await fetch(`${API_URL}/api/alerts/send`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
        'X-Session-ID': SESSION_ID
      },
      body: JSON.stringify({
        priority: logData.alertPriority,
        message: logData.error?.message || 'Unknown error',
        details: logData,
        timestamp: logData.timestamp,
        environment: window.location.hostname
      })
    }).catch(err => {
      // Don't let alert failures crash the app
      console.error('⚠️ Failed to send alert email:', err);
    });
  } catch (error) {
    console.error('⚠️ Alert email system error:', error);
  }
}

// Generic error handler with permission handling
const handleError = (error: any, requestId: number, endpoint: string, startTime: number) => {
  const timeTaken = Date.now() - startTime;

  logger.error(requestId, endpoint, error, timeTaken);

  // If the error is a Response object (fetch API), parse the JSON for details
  if (error.status && error.json) {
    return error.json().then(
      (errorData: any) => {
        const errorMessage = errorData?.detail || `API Error (${error.status}): ${error.statusText}`;
        const errorObj = new Error(errorMessage);
        (errorObj as any).status = error.status;
        // Set error name based on status code
        if (error.status === 403) {
          errorObj.name = 'ForbiddenError';
        }
        // Log the error that will be returned to the caller
        if (DEBUG.enabled && DEBUG.returnValues) {
          console.log(`📦 Error Value #${requestId} will return:`, errorObj);
        }
        throw errorObj;
      },
      () => {
        // If parsing JSON fails, throw with appropriate error type
        const fallbackMessage = error.status === 403 
          ? 'You don\'t have permission to perform this action'
          : `API Error (${error.status}): ${error.statusText}`;
        const errorObj = new Error(fallbackMessage);
        (errorObj as any).status = error.status;
        if (error.status === 403) {
          errorObj.name = 'ForbiddenError';
        }
        // Log the error that will be returned to the caller
        if (DEBUG.enabled && DEBUG.returnValues) {
          console.log(`📦 Error Value #${requestId} will return:`, errorObj);
        }
        throw errorObj;
      },
    );
  }

  // Handle errors thrown as plain objects from fetchAPI (with .status and .data)
  if (error && typeof error === 'object' && 'status' in error) {
    const rawDetail = (error as any).data?.detail || (error as any).data?.message || (error as any).data?.error;
    const detail = typeof rawDetail === 'string' ? rawDetail : (rawDetail ? JSON.stringify(rawDetail) : null);
    const message = detail || `API Error (${(error as any).status}): ${(error as any).statusText || 'Unknown error'}`;
    const errorObj = new Error(message);
    (errorObj as any).status = (error as any).status;
    // Assign a friendly error name based on status
    switch ((error as any).status) {
      case 400:
        errorObj.name = 'BadRequestError';
        break;
      case 401:
        errorObj.name = 'UnauthorizedError';
        break;
      case 403:
        errorObj.name = 'ForbiddenError';
        break;
      case 404:
        errorObj.name = 'NotFoundError';
        break;
      case 409:
        errorObj.name = 'ConflictError';
        break;
      default:
        errorObj.name = 'ApiError';
    }
    if (DEBUG.enabled && DEBUG.returnValues) {
      console.log(`📦 Error Value #${requestId} will return:`, errorObj);
    }
    throw errorObj;
  }

  // For other types of errors
  if (DEBUG.enabled && DEBUG.returnValues) {
    console.log(`📦 Error Value #${requestId} will return:`, error);
  }
  throw error;
};

// Get current user helper
export const getCurrentUser = () => {
  try {
    const isHeaderAuthSession =
      sessionStorage.getItem('botLoginActive') === 'true' ||
      sessionStorage.getItem('testLoginActive') === 'true';
    if (isHeaderAuthSession) {
      const headerUser = sessionStorage.getItem(HEADER_AUTH_USER_KEY);
      if (headerUser) return JSON.parse(headerUser);
    }
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error('Error getting current user:', e);
    return null;
  }
};

// Generic fetch wrapper with error handling, logging, auth headers, and in-flight dedup
const fetchAPI = async (endpoint: string, functionName: string, options?: RequestInit, useCache = false, cacheKey?: string) => {
  // Check cache first if enabled
  if (useCache && cacheKey) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData) {
      const cacheRequestId = ++requestCounter;
      logger.cache('hit', cacheKey);
      logger.returnValue(cacheRequestId, endpoint, functionName, cachedData);
      return cachedData;
    }
    logger.cache('miss', cacheKey);
  }

  // Get current user from localStorage
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;
  const isBotUser = currentUser?.is_bot === true;

  // Determine method (default GET) and build request options with auth header
  const method = (options?.method || 'GET').toUpperCase();
  const baseHeaders: Record<string, string> = {};
  // Copy existing headers
  if (options?.headers) {
    const h = options.headers as Record<string, string>;
    Object.keys(h).forEach(k => { baseHeaders[k] = h[k]; });
  }
  // QA test login mode: always use X-User-ID header, never Bearer token.
  // This prevents stale Auth0 JWTs from overriding the intended QA user identity,
  // which causes "Only the creator can accept or reject applications" errors
  // when the JWT resolves to a different user than the QA test user.
  const isBotLogin = sessionStorage.getItem('botLoginActive') === 'true';
  const isTestLogin = sessionStorage.getItem('testLoginActive') === 'true';
  const fetchToken = resolveAuthToken();
  if (isBotLogin && userId) {
    baseHeaders['X-Bot-User-ID'] = userId.toString();
    const botSecret = sessionStorage.getItem('botAuthSecret');
    if (botSecret) {
      baseHeaders['X-Bot-Auth-Secret'] = botSecret;
    }
  } else if (isTestLogin && userId && !isBotUser) {
    baseHeaders['X-User-ID'] = userId.toString();
    const qaSecret = sessionStorage.getItem('qaAuthSecret');
    if (qaSecret) {
      baseHeaders['X-QA-Secret'] = qaSecret;
    }
  } else if (fetchToken) {
    baseHeaders['Authorization'] = `Bearer ${fetchToken}`;
  } else if (userId && !isBotUser) {
    // Fallback for QA test mode — X-User-ID requires QA secret on backend
    baseHeaders['X-User-ID'] = userId.toString();
    const qaSecret = sessionStorage.getItem('qaAuthSecret');
    if (qaSecret) {
      baseHeaders['X-QA-Secret'] = qaSecret;
    }
  }
  // Anti-CSRF: custom header forces CORS preflight on cross-origin requests
  baseHeaders['X-Requested-With'] = 'XMLHttpRequest';
  const headers = baseHeaders;
  const requestOptions: RequestInit = {
    ...options,
    method,
    headers
  };

  // Build a stable in-flight key for GET requests that are user-scoped
  const inflightKey = method === 'GET' ? `${method}:${endpoint}|user:${userId ?? 'anon'}` : null;

  // The actual network call performer
  const performFetch = async () => {
    const requestId = ++requestCounter;
    const startTime = Date.now();

    // Log the request - safely handle non-JSON bodies (FormData, Blob, etc.)
    let bodyForLogging: any = undefined;
    if (options?.body) {
      if (typeof options.body === 'string') {
        try {
          bodyForLogging = JSON.parse(options.body);
        } catch {
          bodyForLogging = '[non-JSON string body]';
        }
      } else if (options.body instanceof FormData) {
        bodyForLogging = '[FormData]';
      } else if (options.body instanceof Blob) {
        bodyForLogging = '[Blob]';
      } else {
        bodyForLogging = '[unknown body type]';
      }
    }
    logger.request(requestId, method, endpoint, bodyForLogging);

    try {
      const response = await fetch(`${API_URL}${endpoint}`, requestOptions);
      const timeTaken = Date.now() - startTime;

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        logger.error(requestId, endpoint, {
          status: response.status,
          statusText: response.statusText,
          errorData
        }, timeTaken);
        const err: any = new Error(`Request failed: ${response.status} ${response.statusText}`);
        err.status = response.status;
        err.statusText = response.statusText;
        err.data = errorData;
        throw err;
      }

      // For HTTP 204 No Content (often returned by DELETE)
      if (response.status === 204) {
        logger.response(requestId, endpoint, response.status, null, timeTaken);
        logger.returnValue(requestId, endpoint, functionName, null);
        return null;
      }

      const data = await response.json();
      logger.response(requestId, endpoint, response.status, data, timeTaken);

      // Cache the response if enabled
      if (useCache && cacheKey) {
        apiCache.set(cacheKey, data);
        logger.cache('set', cacheKey);
      }

      // Performance tracking
      if (DEBUG.timing) {
        apiPerformance.recordTiming(endpoint, timeTaken);
      }

      // Log the return value explicitly
      logger.returnValue(requestId, endpoint, functionName, data);

      return data;
    } catch (error) {
      // Handle and rethrow
      return handleError(error, requestId, endpoint, startTime);
    } finally {
      // Clear inflight marker when done
      if (inflightKey) {
        inflightRequests.delete(inflightKey);
        if ((logger as any).inflight) logger.inflight('end', inflightKey);
      }
    }
  };

  // For GET requests, deduplicate concurrent identical calls
  if (inflightKey) {
    const existing = inflightRequests.get(inflightKey);
    if (existing) {
      if ((logger as any).inflight) logger.inflight('join', inflightKey);
      return existing;
    }
    const promise = performFetch();
    inflightRequests.set(inflightKey, promise);
    if ((logger as any).inflight) logger.inflight('start', inflightKey);
    return promise;
  }

  // Non-GET: just perform the fetch
  return performFetch();
};

// ---- REFERENCE DATA API ----

export const getCanonicalTopics = async (): Promise<CanonicalTopicOption[]> => {
  const functionName = 'getCanonicalTopics';
  const result = await fetchAPI(
    '/api/topics',
    functionName,
    undefined,
    true,
    'canonical_topics',
  );
  return Array.isArray(result) ? (result as CanonicalTopicOption[]) : [];
};

// ---- SHOOTS API ----

// Get all shoots with optional status filter and creator filter
export const getAllShoots = async (status?: string, useCache = false, forCurrentUser = false, creatorId?: string | number) => {
  const functionName = 'getAllShoots';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with status: ${status || 'undefined'}, forCurrentUser: ${forCurrentUser}, creatorId: ${creatorId || 'undefined'}`);

  let endpoint = forCurrentUser ? '/api/shoots/my-shoots' : '/api/shoots';
  const params = [];
  if (status) {
    params.push(`status=${status}`);
  }
  if (creatorId) {
    params.push(`creator_id=${creatorId}`);
  }
  if (params.length > 0) {
    endpoint += `?${params.join('&')}`;
  }

  const cacheKey = status ? `${CACHE_CONFIG.keys.shoots}_${status}` : CACHE_CONFIG.keys.shoots;
  const result = await fetchAPI(endpoint, functionName, undefined, useCache, cacheKey);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get user's requests (for current user only)
export const getUserRequests = async (userId?: string | number) => {
  const functionName = 'getUserRequests';
  const currentUser = getCurrentUser();
  
  if (!currentUser?.email) {
    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} - No user email available`);
    throw new Error('Your account is missing an email address, so existing requests could not be checked.');
  }

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for user email: ${currentUser.email}`);

  try {
    // Use the existing getAllRequests function with guest email filter
    const result = await getAllRequests(undefined, undefined, false, undefined, currentUser.email);
    
    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
    return { requests: result };
  } catch (error) {
    console.error(`[ERROR] ${functionName} failed:`, error);
    throw error;
  }
};

// Get user's shoots (simulated - returns empty array with message)
export const getUserShoots = async (userId?: string | number) => {
  const functionName = 'getUserShoots';
  const currentUser = getCurrentUser();
  const targetUserId = userId || currentUser?.id;

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for user ID: ${targetUserId}`);

  // Since we can't filter by user on the backend, return a placeholder
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning: [] (User-specific filtering not available)`);
  return {
    shoots: [],
    message: "User-specific shoots will be available soon",
    placeholder: true
  };
};

// Get a specific shoot by ID
export const getShootById = async (shootId: string | number) => {
  const functionName = 'getShootById';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with ID: ${shootId}`);

  const result = await fetchAPI(`/api/shoots/${shootId}`, functionName);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Create a new shoot
export const createShoot = async (shootData: any) => {
  const functionName = 'createShoot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with data:`, shootData);

  // Clear shoots cache on create
  apiCache.clearByKey(CACHE_CONFIG.keys.shoots);
  apiCache.clearByKey(`${CACHE_CONFIG.keys.shoots}_Active`);
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');

  const result = await fetchAPI('/api/shoots', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(shootData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Update an existing shoot
export const updateShoot = async (shootId: string | number, shootData: any) => {
  const functionName = 'updateShoot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${shootId} with data:`, shootData);

  // Clear shoots-related caches on update
  apiCache.clearByPrefix(CACHE_CONFIG.keys.shoots);
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');

  const result = await fetchAPI(`/api/shoots/${shootId}`, functionName, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(shootData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Delete a shoot
export const deleteShoot = async (shootId: string | number) => {
  const functionName = 'deleteShoot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${shootId}`);

  // Clear shoots and explore caches on delete
  apiCache.clearByPrefix(CACHE_CONFIG.keys.shoots);
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');
  apiCache.clearByPrefix('spot_');

  const result = await fetchAPI(`/api/shoots/${shootId}`, functionName, {
    method: 'DELETE',
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Save collaboration suggestions
export const saveCollaborationSuggestions = async (shootId: string, suggestions: any) => {
  const functionName = 'saveCollaborationSuggestions';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for shoot ID: ${shootId}`);
  
  return fetchAPI(`/api/shoots/${shootId}/suggestions`, functionName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suggestions }),
  });
};

// Get collaboration suggestions
export const getCollaborationSuggestions = async (shootId: string) => {
  const functionName = 'getCollaborationSuggestions';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for shoot ID: ${shootId}`);
  
  const response = await fetchAPI(`/api/shoots/${shootId}/suggestions`, functionName);
  // Extract the suggestions array from the response
  return response.suggestions || [];
};

// ---- REQUESTS API ----

// Get all requests with optional filtering
export const getAllRequests = async (
  status?: string,
  shootId?: string | number,
  useCache = false,
  creatorId?: number,
  guestEmail?: string,
) => {
  const functionName = 'getAllRequests';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with status: ${status || 'undefined'}, shootId: ${shootId || 'undefined'}, creatorId: ${creatorId || 'undefined'}, guestEmail: ${guestEmail || 'undefined'}`);

  let endpoint = '/api/requests';
  const params = new URLSearchParams();

  // Normalize and validate inputs to prevent invalid requests (e.g., email passed as creatorId)
  const normalizedCreatorId = (creatorId !== undefined && creatorId !== null && Number.isFinite(Number(creatorId)))
    ? Number(creatorId)
    : undefined;
  const normalizedGuestEmail = guestEmail ? String(guestEmail).trim() : undefined;

  if (creatorId !== undefined && creatorId !== null && normalizedCreatorId === undefined) {
    console.warn(`[WARN] ${functionName}: Ignoring invalid creatorId; expected a number but received`, creatorId);
  }

  if (status) {
    params.append('status', status);
  }

  if (shootId) {
    params.append('shoot_id', shootId.toString());
  }

  if (normalizedCreatorId !== undefined) {
    params.append('creator_id', String(normalizedCreatorId));
  }

  if (normalizedGuestEmail) {
    params.append('guest_email', normalizedGuestEmail);
  }

  const queryString = params.toString();
  if (queryString) {
    endpoint += `?${queryString}`;
  }

  const cacheKey = `${CACHE_CONFIG.keys.requests}_${status || 'all'}_${shootId || 'all'}_${normalizedCreatorId || 'all'}_${normalizedGuestEmail || 'all'}`;
  const result = redactApplicationMessages(
    await fetchAPI(endpoint, functionName, undefined, useCache, cacheKey),
  );
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

export const withdrawApplication = async (applicationId: number): Promise<any> => {
  const functionName = 'withdrawApplication';
  const result = await fetchAPI(
    `/api/guest/requests/${applicationId}/withdraw`,
    functionName,
    { method: 'POST' },
  );
  apiCache.clearByPrefix(CACHE_CONFIG.keys.requests);
  apiCache.clearByPrefix('cached_user_requests');
  return result;
};

export const declineAcceptedApplication = async (applicationId: number): Promise<any> => {
  const functionName = 'declineAcceptedApplication';
  const result = await fetchAPI(
    `/api/guest/requests/${applicationId}/decline-confirmation`,
    functionName,
    { method: 'POST' },
  );
  apiCache.clearByPrefix(CACHE_CONFIG.keys.requests);
  apiCache.clearByPrefix('cached_user_requests');
  return result;
};

// Get request statistics (simulated based on available data)
export const getRequestStats = async () => {
  const functionName = 'getRequestStats';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called`);

  try {
    // Get all requests to calculate stats
    const response = await getAllRequests(undefined, undefined, true);
    // Handle both array and object responses (API may return { requests: [...] } or [...])
    const allRequests = Array.isArray(response) ? response : (response?.requests || response?.items || []);

    const stats = {
      total: allRequests.length,
      pending: allRequests.filter((app: any) => app.status === 'pending').length,
      accepted: allRequests.filter((app: any) => app.status === 'accepted').length,
      rejected: allRequests.filter((app: any) => app.status === 'rejected').length,
      isPlaceholder: true,
      message: "Showing platform-wide statistics"
    };

    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, stats);
    return stats;
  } catch (error) {
    console.error(`Error calculating request stats:`, error);
    return {
      total: 0,
      pending: 0,
      accepted: 0,
      rejected: 0,
      isPlaceholder: true,
      error: true
    };
  }
};

// Get shoot statistics (simulated based on available data)
export const getShootStats = async () => {
  const functionName = 'getShootStats';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called`);

  try {
    // Get all shoots to calculate stats
    const response = await getAllShoots(undefined, true);
    // Handle both array and object responses (API may return { shoots: [...] } or [...])
    const allShoots = Array.isArray(response) ? response : (response?.shoots || response?.items || []);

    const stats = {
      total: allShoots.length,
      active: allShoots.filter((shoot: any) => shoot.status === 'Active').length,
      draft: allShoots.filter((shoot: any) => shoot.status === 'Draft').length,
      completed: allShoots.filter((shoot: any) => shoot.status === 'Completed').length,
      totalGuestSpots: allShoots.reduce((sum: number, shoot: any) => sum + (shoot.guestSpots || 0), 0),
      totalGuests: allShoots.reduce((sum: number, shoot: any) => sum + (shoot.guests || 0), 0),
      isPlaceholder: true,
      message: "Showing platform-wide statistics"
    };

    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, stats);
    return stats;
  } catch (error) {
    console.error(`Error calculating shoot stats:`, error);
    return {
      total: 0,
      active: 0,
      draft: 0,
      completed: 0,
      totalGuestSpots: 0,
      totalGuests: 0,
      isPlaceholder: true,
      error: true
    };
  }
};

// Get all guest spots for a shoot
export const getGuestSpotsByShoot = async (shootId: string | number) => {
  const functionName = 'getGuestSpotsByShoot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for shoot ID: ${shootId}`);

  const result = await fetchAPI(`/api/shoots/${shootId}/spots`, functionName);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get a specific guest spot
export const getGuestSpotById = async (spotId: string | number) => {
  const functionName = 'getGuestSpotById';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${spotId}`);

  const result = await fetchAPI(`/api/spots/${spotId}`, functionName);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get explore guest spots
export const getExploreGuestSpots = async (
  options: {
    page?: number;
    limit?: number;
    sort_by?: 'scheduledDate' | 'guestsCount' | 'newest';
    format?: string;
    date?: string;  // 'active' | 'past' | 'all'
    search?: string;
    monetization?: string;  // 'guest-pays' | 'host-pays' | 'none' | 'all'
    show_my_confirmed?: boolean;
    useCache?: boolean;
  } = {}
) => {
  const functionName = 'getExploreGuestSpots';
  const { page = 1, limit = 20, sort_by = 'scheduledDate', format, date, search, monetization, show_my_confirmed = false, useCache = false } = options;
  
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with options:`, options);

  // Build query parameters
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  params.append('sort_by', sort_by);
  params.append('show_my_confirmed', show_my_confirmed.toString());
  params.append('timezone', BROWSER_TIMEZONE);
  
  if (format && format !== 'all') {
    params.append('format', format);
  }
  
  if (date && date !== 'all') {
    params.append('date', date);
  }
  
  if (search && search.trim()) {
    params.append('search', search.trim());
  }

  if (monetization && monetization !== 'all') {
    params.append('monetization', monetization);
  }

  // Use a user-aware cache key so anon vs logged-in (and different users)
  // don't share cached explore results. This leverages the X-User-ID header
  // filtering on the backend to exclude the current user's own spots.
  const currentUser = getCurrentUser();
  const userKey = currentUser?.id != null ? String(currentUser.id) : 'anon';
  const cacheKey = `explore_spots_user:${userKey}_tz:${BROWSER_TIMEZONE}_page:${page}_limit:${limit}_sort:${sort_by}_fmt:${format || 'all'}_date:${date || 'all'}_search:${search || ''}_mon:${monetization || 'all'}_showconf:${show_my_confirmed}`;

  const endpoint = `/api/spots/explore-spots?${params.toString()}`;
  const result = await fetchAPI(endpoint, functionName, undefined, useCache, cacheKey);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get bucket counts for every Explore filter chip (compensation + date)
// Returns { compensation: { all, none, 'guest-pays', 'host-pays' }, date: { all, active, past } }.
// Compensation counts cross-filter by the active date filter (and vice versa), so each
// chip shows what selecting it would yield.
export const getExploreFilterCounts = async (
  options: {
    format?: string;
    date?: string;        // 'active' | 'past' | 'all'
    search?: string;
    monetization?: string; // 'guest-pays' | 'host-pays' | 'none' | 'all'
    show_my_confirmed?: boolean;
    useCache?: boolean;
  } = {}
): Promise<{
  compensation: Record<string, number>;
  date: Record<string, number>;
}> => {
  const functionName = 'getExploreFilterCounts';
  const { format, date, search, monetization, show_my_confirmed = false, useCache = true } = options;

  const params = new URLSearchParams();
  params.append('show_my_confirmed', show_my_confirmed.toString());
  params.append('timezone', BROWSER_TIMEZONE);
  if (format && format !== 'all') params.append('format', format);
  if (date && date !== 'all') params.append('date', date);
  if (search && search.trim()) params.append('search', search.trim());
  if (monetization && monetization !== 'all') params.append('monetization', monetization);

  const currentUser = getCurrentUser();
  const userKey = currentUser?.id != null ? String(currentUser.id) : 'anon';
  const cacheKey = `explore_filter_counts_user:${userKey}_tz:${BROWSER_TIMEZONE}_fmt:${format || 'all'}_date:${date || 'all'}_search:${search || ''}_mon:${monetization || 'all'}_showconf:${show_my_confirmed}`;

  const endpoint = `/api/spots/filter-counts?${params.toString()}`;
  const result = await fetchAPI(endpoint, functionName, undefined, useCache, cacheKey);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get guest spot statistics
export const getGuestSpotStatistics = async (useCache = true) => {
  const functionName = 'getGuestSpotStatistics';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called`);
  
  const currentUser = getCurrentUser();
  const userKey = currentUser?.id != null ? String(currentUser.id) : 'anon';
  const cacheKey = `spot_statistics_user:${userKey}`;
  
  const result = await fetchAPI('/api/spots/stats', functionName, undefined, useCache, cacheKey);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get similar guest spot recommendations
export const getSimilarGuestSpots = async (spotId: number, limit = 5, useCache = true) => {
  const functionName = 'getSimilarGuestSpots';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for spot:`, spotId);
  
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  
  const currentUser = getCurrentUser();
  const userKey = currentUser?.id != null ? String(currentUser.id) : 'anon';
  const cacheKey = `spot_recommendations_${spotId}_user:${userKey}_limit:${limit}`;
  
  const endpoint = `/api/spots/${spotId}/recommendations?${params.toString()}`;
  const result = await fetchAPI(endpoint, functionName, undefined, useCache, cacheKey);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Create a new guest spot
export const createGuestSpot = async (spotData: any) => {
  const functionName = 'createGuestSpot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with data:`, spotData);

  // Clear relevant caches
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');

  const result = await fetchAPI('/api/spots', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(spotData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Update a guest spot
export const updateGuestSpot = async (spotId: string | number, spotData: any) => {
  const functionName = 'updateGuestSpot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${spotId} with data:`, spotData);

  // Clear spot and explore caches on update
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');
  apiCache.clearByPrefix('spot_');
  apiCache.clearByPrefix(CACHE_CONFIG.keys.shoots);

  const result = await fetchAPI(`/api/spots/${spotId}`, functionName, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(spotData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Delete a guest spot
export const deleteGuestSpot = async (spotId: string | number) => {
  const functionName = 'deleteGuestSpot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${spotId}`);

  // Clear spot and explore caches on delete
  apiCache.clearByPrefix('explore_spots');
  apiCache.clearByPrefix('explore_filter_counts');
  apiCache.clearByPrefix('spot_');
  apiCache.clearByPrefix(CACHE_CONFIG.keys.shoots);

  const result = await fetchAPI(`/api/spots/${spotId}`, functionName, {
    method: 'DELETE',
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get requests for a guest spot
export const getRequestsForSpot = async (spotId: string | number) => {
  const functionName = 'getRequestsForSpot';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for spot ID: ${spotId}`);

  const result = redactApplicationMessages(
    await fetchAPI(`/api/spots/${spotId}/requests`, functionName),
  );
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Create a request for a guest spot
export const createRequest = async (spotId: string | number, requestData: any) => {
  const functionName = 'createRequest';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for spot ID: ${spotId} with data:`, requestData);

  // Clear requests cache
  apiCache.clearByKey(CACHE_CONFIG.keys.requests);

  const result = await fetchAPI(`/api/spots/${spotId}/requests`, functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-ID': requestData.acquisition_session_id || SESSION_ID,
    },
    body: JSON.stringify(requestData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get a specific request by ID
export const getRequestById = async (requestId: string | number) => {
  const functionName = 'getRequestById';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${requestId}`);

  const result = redactApplicationMessages(
    await fetchAPI(`/api/requests/${requestId}`, functionName),
  );
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Update a request's status
export const updateRequestStatus = async (requestId: string | number, data: { status: string; cancellation_reason?: string; rejection_reason?: string; rejection_note?: string }) => {
  const functionName = 'updateRequestStatus';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${requestId} with status: ${data.status}`);

  // Clear requests-related caches on status change
  apiCache.clearByPrefix(CACHE_CONFIG.keys.requests);

  const result = await fetchAPI(`/api/requests/${requestId}/status`, functionName, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Delete a request
export const deleteRequest = async (requestId: string | number) => {
  const functionName = 'deleteRequest';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for ID: ${requestId}`);

  // Clear requests-related caches on delete
  apiCache.clearByPrefix(CACHE_CONFIG.keys.requests);

  const result = await fetchAPI(`/api/requests/${requestId}`, functionName, {
    method: 'DELETE',
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// ---- USERS API ----

// Create a new user (signup)
export const createUser = async (userData: any) => {
  const functionName = 'createUser';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with data:`, userData);

  const result = await fetchAPI('/api/users', functionName, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(userData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Send the one-time email code required when a social provider (most commonly
// Facebook) does not return a verified email address. The code is consumed by
// POST /api/users together with the still-authenticated social identity.
export const sendSignupEmailVerificationCode = async (email: string) => {
  return fetchAPI(
    '/api/auth/passwordless/send-otp',
    'sendSignupEmailVerificationCode',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    }
  );
};

// Login a user
export const loginUser = async (loginData: { username: string }) => {
  const functionName = 'loginUser';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for username: ${loginData.username}`);

  const result = await fetchAPI('/api/users/login', functionName, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(loginData),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Get user by ID
export const getUser = async (userId: string | number) => {
  const functionName = 'getUser';
  const endpoint = `/api/users/${userId}`;
  const result = await fetchAPI(endpoint, functionName);
  logger.returnValue(requestCounter, endpoint, functionName, result);
  return result;
};

// Get user by username
export const getUserByUsername = async (username: string) => {
  const result = await fetchAPI(`/api/users/${username}`, 'getUserByUsername');
  logger.returnValue(requestCounter, `/api/users/${username}`, 'getUserByUsername', result);
  return result;
};

// Get host trust signals: response time, acceptance rate, completed collabs
export const getHostStats = async (username: string) => {
  const result = await fetchAPI(`/api/users/${username}/host-stats`, 'getHostStats');
  return result;
};

// Get user by email (exact match)
export const getUserByEmail = async (email: string) => {
  const functionName = 'getUserByEmail';
  const endpoint = `/api/users/by_email/lookup?email=${encodeURIComponent(email)}`;
  const result = await fetchAPI(endpoint, functionName);
  logger.returnValue(requestCounter, endpoint, functionName, result);
  return result;
};

// Get user by Auth0 ID
export const getUserByAuth0Id = async (auth0Id: string) => {
  const functionName = 'getUserByAuth0Id';
  const endpoint = `/api/users/by_auth0_id/${encodeURIComponent(auth0Id)}`;
  const result = await fetchAPI(endpoint, functionName);
  logger.returnValue(requestCounter, endpoint, functionName, result);
  return result;
};

// Check if username is available
export const checkUsernameAvailability = async (username: string): Promise<boolean> => {
  const functionName = 'checkUsernameAvailability';
  const endpoint = `/api/users/username-availability?username=${encodeURIComponent(username.trim())}`;
  const result = await fetchAPI(endpoint, functionName);
  return result?.available === true;
};

// Get all collaborators for collaboration suggestions
export const getAllCollaborators = async () => {
  const functionName = 'getAllCollaborators';
  const result = await fetchAPI('/api/users?role=collaborator', functionName);
  logger.returnValue(requestCounter, '/api/users?role=collaborator', functionName, result);
  return result;
};

// Update user profile
export const updateUserProfile = async (userId: string | number, profileData: any) => {
  // Clear relevant caches when updating profile
  apiCache.clearByKey('cached_user_' + userId);
  
  const result = await fetchAPI(
    `/api/users/${userId}/profile`,
    'updateUserProfile',
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    }
  );

  // Topic edits change personalized ranking and overlap explanations. Do not
  // serve this user a cached pre-edit Explore page after the profile saves.
  apiCache.clearByPrefix(`explore_spots_user:${userId}_`);
  
  logger.returnValue(requestCounter, `/api/users/${userId}/profile`, 'updateUserProfile', result);
  return result;
};

// Logout a user
export const logoutUser = async () => {
  const functionName = 'logoutUser';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called`);

  // Clear all caches on logout
  apiCache.clear();

  // In a real app with a backend auth system, you'd also call something like:
  // return fetchAPI('/users/logout', { method: 'POST' });

  // But for now, just clear local state
  localStorage.removeItem('currentUser');
  if (DEBUG.enabled) console.log(`[DEBUG] Removed currentUser from localStorage`);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning: true`);

  return true;
};

// ---- FILE UPLOAD HELPER ----

// Upload a file (for shoot images) to Cloudinary via backend
export const uploadFile = async (file: File, folder: string = 'general') => {
  const functionName = 'uploadFile';
  const requestId = ++requestCounter;
  const startTime = Date.now();

  // COMPREHENSIVE FILE METADATA LOGGING
  const fileMetadata = {
    name: file.name,
    size: file.size,
    sizeFormatted: `${(file.size / 1024).toFixed(2)} KB`,
    type: file.type,
    mimeType: file.type,
    extension: file.name.split('.').pop(),
    lastModified: new Date(file.lastModified).toISOString(),
    folder: folder,
    requestId,
    sessionId: SESSION_ID
  };

  if (DEBUG.enabled) {
    console.log(`📤 [UPLOAD START] Request #${requestId}`);
    console.log('📊 FILE METADATA:', JSON.stringify(fileMetadata, null, 2));
  }

  // STATE: INIT → VALIDATING
  logger.stateTransition(requestId, 'uploadFile', 'INIT', 'VALIDATING', fileMetadata);

  try {
    // Validation checks
    const validationStart = Date.now();
    if (!file || !file.name) {
      throw new Error('Invalid file: file or filename is missing');
    }
    if (file.size === 0) {
      throw new Error('Invalid file: file is empty');
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      throw new Error(`File too large: ${fileMetadata.sizeFormatted} exceeds 10MB limit`);
    }
    const validationTime = Date.now() - validationStart;

    // STATE: VALIDATING → PREPARING
    logger.stateTransition(requestId, 'uploadFile', 'VALIDATING', 'PREPARING', { 
      validationTime,
      validationPassed: true 
    });

    // Create FormData for file upload
    const prepareStart = Date.now();
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = `/api/upload/image?folder=${encodeURIComponent(folder)}`;
    const prepareTime = Date.now() - prepareStart;

    // STATE: PREPARING → UPLOADING
    logger.stateTransition(requestId, 'uploadFile', 'PREPARING', 'UPLOADING', {
      endpoint,
      formDataSize: file.size
    });

    const requestMetadata = {
      filename: file.name,
      size: file.size,
      type: file.type,
      folder: folder,
      endpoint: endpoint,
      fullUrl: `${API_URL}${endpoint}`
    };
    
    logger.request(requestId, 'POST', endpoint, requestMetadata);

    // Execute upload
    const uploadStart = Date.now();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
    });
    const uploadTime = Date.now() - uploadStart;

    // STATE: UPLOADING → PROCESSING_RESPONSE
    logger.stateTransition(requestId, 'uploadFile', 'UPLOADING', 'PROCESSING_RESPONSE', {
      status: response.status,
      statusText: response.statusText,
      uploadTime
    });

    const timeTaken = Date.now() - startTime;

    if (!response.ok) {
      // STATE: PROCESSING_RESPONSE → FAILED
      logger.stateTransition(requestId, 'uploadFile', 'PROCESSING_RESPONSE', 'FAILED', {
        status: response.status,
        statusText: response.statusText
      });

      const errorData = await response.json().catch(() => null);
      const priority = response.status >= 500 ? AlertPriority.P1_HIGH : AlertPriority.P2_MEDIUM;
      logger.error(requestId, endpoint, {
        status: response.status,
        statusText: response.statusText,
        errorData,
        fileMetadata
      }, timeTaken, priority);
      throw new Error(errorData?.detail || `Upload failed: ${response.status} ${response.statusText}`);
    }

    const parseStart = Date.now();
    const data = await response.json();
    const parseTime = Date.now() - parseStart;

    // Log response headers if available
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    logger.response(requestId, endpoint, response.status, data, timeTaken, responseHeaders);

    // STATE: PROCESSING_RESPONSE → SUCCESS
    logger.stateTransition(requestId, 'uploadFile', 'PROCESSING_RESPONSE', 'SUCCESS', {
      cloudinaryUrl: data.secure_url || data.url,
      publicId: data.public_id,
      dimensions: `${data.width}x${data.height}`
    });

    // Return in expected format
    const result = {
      url: data.secure_url || data.url,
      filename: file.name,
      public_id: data.public_id,
      width: data.width,
      height: data.height,
      format: data.format
    };

    // PERFORMANCE BREAKDOWN
    logger.performanceBreakdown(requestId, 'uploadFile', {
      validation: validationTime,
      preparation: prepareTime,
      upload: uploadTime,
      responseParse: parseTime,
      total: timeTaken
    });

    if (DEBUG.enabled) {
      console.log(`✅ [UPLOAD SUCCESS] Request #${requestId} - ${timeTaken}ms`);
      console.log('📊 RESULT:', JSON.stringify(result, null, 2));
    }

    logger.returnValue(requestId, endpoint, functionName, result);
    return result;
  } catch (error: any) {
    const timeTaken = Date.now() - startTime;
    
    // STATE: ANY → ERROR
    logger.stateTransition(requestId, 'uploadFile', 'ANY', 'ERROR', {
      errorMessage: error.message,
      errorName: error.name
    });

    const priority = error.message?.includes('500') || error.message?.includes('network') 
      ? AlertPriority.P1_HIGH 
      : AlertPriority.P2_MEDIUM;
    
    logger.error(requestId, '/api/upload/image', {
      ...error,
      fileMetadata,
      attemptedOperation: 'uploadFile'
    }, timeTaken, priority);

    if (DEBUG.enabled) {
      console.error(`❌ [UPLOAD FAILED] Request #${requestId} - ${timeTaken}ms`);
      console.error('📊 ERROR DETAILS:', { error, fileMetadata });
    }

    throw error;
  }
};

// Delete an image from Cloudinary
export const deleteImage = async (publicId: string) => {
  const functionName = 'deleteImage';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with publicId: ${publicId}`);

  const requestId = ++requestCounter;
  const startTime = Date.now();

  try {
    const endpoint = `/api/upload/image/${encodeURIComponent(publicId)}`;
    
    logger.request(requestId, 'DELETE', endpoint, { publicId });

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const timeTaken = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      logger.error(requestId, endpoint, {
        status: response.status,
        statusText: response.statusText,
        errorData
      }, timeTaken);
      throw new Error(errorData?.detail || `Delete failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    logger.response(requestId, endpoint, response.status, data, timeTaken);

    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, data);
    logger.returnValue(requestId, endpoint, functionName, data);
    return data;
  } catch (error: any) {
    const timeTaken = Date.now() - startTime;
    logger.error(requestId, '/api/upload/image', error, timeTaken);
    throw error;
  }
};

// ---- VIDEO PITCH UPLOAD ----

// Upload a video pitch (max 15 seconds) to Cloudinary via backend
export const uploadVideoPitch = async (file: File): Promise<{
  secure_url: string;
  public_id: string;
  thumbnail_url: string;
  duration: number;
  width?: number;
  height?: number;
  format?: string;
  filename?: string;
}> => {
  const functionName = 'uploadVideoPitch';
  const requestId = ++requestCounter;
  const startTime = Date.now();

  // File metadata for logging
  const fileMetadata = {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified
  };

  if (DEBUG.enabled) {
    console.log(`[DEBUG] ${functionName} called with:`, fileMetadata);
  }

  logger.request(requestId, 'POST', '/api/upload/video', {
    fileMetadata,
    operation: 'uploadVideoPitch'
  });

  try {
    // Client-side validation
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
    if (file.size > MAX_VIDEO_SIZE) {
      throw new Error(`Video size exceeds ${MAX_VIDEO_SIZE / (1024 * 1024)}MB limit`);
    }

    const validFormats = ['mp4', 'mov', 'webm', 'avi'];
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !validFormats.includes(fileExt)) {
      throw new Error(`Invalid format. Allowed: ${validFormats.join(', ')}`);
    }

    logger.stateTransition(requestId, 'uploadVideoPitch', 'INIT', 'PREPARING', { validationPassed: true });

    // Prepare FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'video_pitches');

    const endpoint = `${API_URL}/api/upload/video`;

    logger.stateTransition(requestId, 'uploadVideoPitch', 'PREPARING', 'UPLOADING', { endpoint });

    const uploadStart = Date.now();
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    const uploadTime = Date.now() - uploadStart;

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      logger.stateTransition(requestId, 'uploadVideoPitch', 'UPLOADING', 'FAILED', {
        status: response.status,
        error: errorData
      });
      
      throw new Error(errorData?.detail || `Upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const timeTaken = Date.now() - startTime;

    logger.response(requestId, endpoint, response.status, data, timeTaken);

    logger.stateTransition(requestId, 'uploadVideoPitch', 'UPLOADING', 'SUCCESS', {
      videoUrl: data.secure_url,
      duration: data.duration,
      thumbnail: data.thumbnail_url
    });

    logger.performanceBreakdown(requestId, 'uploadVideoPitch', {
      validation: 10, // Approximate
      upload: uploadTime,
      total: timeTaken
    });

    if (DEBUG.enabled) {
      console.log(`✅ [VIDEO UPLOAD SUCCESS] Request #${requestId} - ${timeTaken}ms`);
      console.log('📹 Video Details:', {
        url: data.secure_url,
        duration: `${data.duration}s`,
        size: `${data.width}x${data.height}`
      });
    }

    logger.returnValue(requestId, endpoint, functionName, {
      ...data,
      uploadTime: timeTaken
    });

    return data;
  } catch (error: any) {
    const timeTaken = Date.now() - startTime;
    
    const priority = error.message?.includes('size exceeds') 
      ? AlertPriority.P3_LOW
      : error.message?.includes('format')
      ? AlertPriority.P3_LOW
      : AlertPriority.P2_MEDIUM;
    
    logger.error(requestId, '/api/upload/video', {
      ...error,
      fileMetadata,
      attemptedOperation: 'uploadVideoPitch'
    }, timeTaken, priority);

    if (DEBUG.enabled) {
      console.error(`❌ [VIDEO UPLOAD FAILED] Request #${requestId} - ${timeTaken}ms`);
      console.error('📊 ERROR DETAILS:', { error, fileMetadata });
    }

    throw error;
  }
};

// Delete a video pitch from Cloudinary
export const deleteVideoPitch = async (publicId: string) => {
  const functionName = 'deleteVideoPitch';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called with publicId: ${publicId}`);

  const requestId = ++requestCounter;
  const startTime = Date.now();

  try {
    const endpoint = `/api/upload/video/${encodeURIComponent(publicId)}`;
    
    logger.request(requestId, 'DELETE', endpoint, { publicId });

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const timeTaken = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      logger.error(requestId, endpoint, {
        status: response.status,
        statusText: response.statusText,
        errorData
      }, timeTaken);
      throw new Error(errorData?.detail || `Delete failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    logger.response(requestId, endpoint, response.status, data, timeTaken);

    if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, data);
    logger.returnValue(requestId, endpoint, functionName, data);
    return data;
  } catch (error: any) {
    const timeTaken = Date.now() - startTime;
    logger.error(requestId, '/api/upload/video', error, timeTaken);
    throw error;
  }
};

export const getShootByUrl = async (username: string, shootSlug: string) => {
  const functionName = 'getShootByUrl';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for username: ${username}, shootSlug: ${shootSlug}`);

  const result = await fetchAPI(`/api/shoots/creator/${username}/${shootSlug}`, functionName);
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

export const claimRequest = async (requestId: string | number, userId: string | number) => {
  const functionName = 'claimRequest';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called for request ID: ${requestId}, user ID: ${userId}`);

  const result = await fetchAPI(`/api/requests/${requestId}/claim`, functionName, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId }),
  });

  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// ---- SOCIAL MEDIA PROFILE PIC FETCHING ----

// Fetch profile picture from social media platform
export const fetchSocialProfilePic = async (platform: string, username?: string, url?: string) => {
  const functionName = 'fetchSocialProfilePic';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called - Platform: ${platform}, Username: ${username}, URL: ${url}`);

  let endpoint = `/api/social/profile-pic?platform=${encodeURIComponent(platform)}`;
  if (username) {
    endpoint += `&username=${encodeURIComponent(username)}`;
  }
  if (url) {
    endpoint += `&url=${encodeURIComponent(url)}`;
  }

  const result = await fetchAPI(endpoint, functionName, {
    headers: getAuthHeaders(),
  });
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Import social media profile pic to Cloudinary
export const importSocialProfilePic = async (platform: string, username?: string, url?: string) => {
  const functionName = 'importSocialProfilePic';
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} called - Platform: ${platform}, Username: ${username}, URL: ${url}`);

  let endpoint = `/api/social/import-profile-pic?platform=${encodeURIComponent(platform)}`;
  if (username) {
    endpoint += `&username=${encodeURIComponent(username)}`;
  }
  if (url) {
    endpoint += `&url=${encodeURIComponent(url)}`;
  }

  const result = await fetchAPI(endpoint, functionName, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  
  if (DEBUG.enabled) console.log(`[DEBUG] ${functionName} returning:`, result);
  return result;
};

// Performance monitoring utility
export const apiPerformance = {
  timings: {} as Record<string, number[]>,

  recordTiming: (endpoint: string, time: number) => {
    if (!DEBUG.enabled || !DEBUG.timing) return;

    if (!apiPerformance.timings[endpoint]) {
      apiPerformance.timings[endpoint] = [];
    }
    apiPerformance.timings[endpoint].push(time);

    // Keep only the last 100 records per endpoint
    if (apiPerformance.timings[endpoint].length > 100) {
      apiPerformance.timings[endpoint].shift();
    }
  },

  getAverageTime: (endpoint: string) => {
    if (!apiPerformance.timings[endpoint] || apiPerformance.timings[endpoint].length === 0) {
      return 0;
    }

    const sum = apiPerformance.timings[endpoint].reduce((a, b) => a + b, 0);
    return sum / apiPerformance.timings[endpoint].length;
  },

  logPerformance: () => {
    if (!DEBUG.enabled || !DEBUG.timing) return;

    console.group('📊 API Performance Metrics');
    for (const [endpoint, timings] of Object.entries(apiPerformance.timings)) {
      if (timings.length === 0) continue;

      const avg = apiPerformance.getAverageTime(endpoint);
      const min = Math.min(...timings);
      const max = Math.max(...timings);

      console.log(`${endpoint}: Avg ${avg.toFixed(2)}ms, Min ${min}ms, Max ${max}ms, Calls: ${timings.length}`);
    }
    console.groupEnd();
  },

  reset: () => {
    apiPerformance.timings = {};
  }
};

// Log performance metrics every 5 minutes if timing is enabled
if (DEBUG.enabled && DEBUG.timing) {
  setInterval(() => {
    apiPerformance.logPerformance();
  }, 5 * 60 * 1000);
}

// Clear cache function for external use
export const clearAPICache = () => {
  apiCache.clear();
  if (DEBUG.enabled) console.log('API cache cleared');
};

// Score requests that don't have AI scores yet
export const scoreUnscoredRequests = async () => {
  const functionName = 'scoreUnscoredRequests';
  return fetchAPI('/api/requests/score_unscored', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Rescore all requests (overwrite existing scores)
export const rescoreAllRequests = async () => {
  const functionName = 'rescoreAllRequests';
  return fetchAPI('/api/requests/rescore_all', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Start job-based scoring of unscored requests; returns { job_id }
export const startScoreUnscored = async (): Promise<{ job_id: string; status: string }> => {
  const functionName = 'startScoreUnscored';
  return fetchAPI('/api/requests/start_score_unscored', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Start job-based rescoring; returns { job_id }
export const startRescoreAll = async (): Promise<{ job_id: string; status: string }> => {
  const functionName = 'startRescoreAll';
  return fetchAPI('/api/requests/start_rescore_all', functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Get job status by id
export const getScoringJobStatus = async (jobId: string): Promise<{
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled';
  mode: 'unscored' | 'rescore';
  total_found: number;
  processed: number;
  scored: number;
  failed: number;
  percent: number;
  errors: string[];
  started_at: string;
  updated_at: string;
}> => {
  const functionName = 'getScoringJobStatus';
  return fetchAPI(`/api/requests/job_status/${jobId}`, functionName, {
    method: 'GET',
  });
};

export const scoreRequest = (requestId: number): Promise<any> => {
  const functionName = 'scoreRequest';
  return fetchAPI(`/api/requests/${requestId}/score`, functionName, {
    method: 'POST',
  });
};

export const startScoreRequest = (requestId: number): Promise<any> => {
  const functionName = 'startScoreRequest';
  return fetchAPI(`/api/requests/${requestId}/start_score`, functionName, {
    method: 'POST',
  });
};

export const getRequestScoringStatus = (requestId: number): Promise<any> => {
  const functionName = 'getRequestScoringStatus';
  return fetchAPI(`/api/requests/${requestId}/scoring_status`, functionName, {
    method: 'GET',
  });
};

// ============================================================================
// TIME SLOT MANAGEMENT APIS
// ============================================================================

export const getTimeSlotsForSpot = (guestSpotId: number): Promise<any> => {
  const functionName = 'getTimeSlotsForSpot';
  return fetchAPI(`/api/time-slots/spot/${guestSpotId}`, functionName);
};

export const createTimeSlot = (data: {
  guest_spot_id: number;
  slot_date: string;
  start_time: string;
  end_time: string;
  capacity?: number;
  label?: string;
  notes?: string;
}): Promise<any> => {
  const functionName = 'createTimeSlot';
  return fetchAPI('/api/time-slots/', functionName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const updateTimeSlot = (timeSlotId: number, data: {
  slot_date?: string;
  start_time?: string;
  end_time?: string;
  capacity?: number;
  label?: string;
  notes?: string;
}): Promise<any> => {
  const functionName = 'updateTimeSlot';
  return fetchAPI(`/api/time-slots/${timeSlotId}`, functionName, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

export const deleteTimeSlot = (timeSlotId: number): Promise<any> => {
  const functionName = 'deleteTimeSlot';
  return fetchAPI(`/api/time-slots/${timeSlotId}`, functionName, {
    method: 'DELETE',
  });
};

// ============================================================================
// BOOKING CONFIRMATION APIS
// ============================================================================

export const confirmBooking = (
  requestId: number,
  confirmationToken: string,
): Promise<any> => {
  const functionName = 'confirmBooking';
  const promise = fetchAPI(`/api/requests/${requestId}/confirm_booking`, functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      confirmation_token: confirmationToken,
    }),
  });
  
  return promise;
};

export const resendAcceptanceEmail = (requestId: number): Promise<any> => {
  const functionName = 'resendAcceptanceEmail';
  return fetchAPI(`/api/requests/${requestId}/resend_acceptance_email`, functionName, {
    method: 'POST',
  });
};

export const proposeTimes = (requestId: number, proposedTimes: string[]): Promise<any> => {
  const functionName = 'proposeTimes';
  return fetchAPI(`/api/requests/${requestId}/propose_times`, functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ proposed_times: proposedTimes }),
  });
};

export const getProposedTimes = (requestId: number): Promise<any> => {
  const functionName = 'getProposedTimes';
  return fetchAPI(`/api/requests/${requestId}/proposed_times`, functionName, {
    method: 'GET',
  });
};

export const selectTime = (requestId: number, proposedTimeId: number): Promise<any> => {
  const functionName = 'selectTime';
  return fetchAPI(`/api/requests/${requestId}/select_time`, functionName, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ proposed_time_id: proposedTimeId }),
  });
};

// ============ Collab Performance APIs ============

export const markCollabComplete = (
  requestId: number,
  creatorId: number,
  options: {
    videoUrl?: string;
    videoPlatform?: string;
    videoTitle?: string;
    performanceTier?: string;
    estimatedViews?: number;
    overallRating?: number;
    preparationRating?: number;
    punctuality?: string;
    promotion?: string;
    wouldCollabAgain?: string;
    feedbackText?: string;
  } = {}
): Promise<any> => {
  const functionName = 'markCollabComplete';
  return fetchAPI(`/api/collab-performance/mark-complete?creator_id=${creatorId}`, functionName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      application_id: requestId,
      video_url: options.videoUrl,
      video_platform: options.videoPlatform,
      video_title: options.videoTitle,
      performance_tier: options.performanceTier,
      estimated_views: options.estimatedViews,
      overall_rating: options.overallRating,
      preparation_rating: options.preparationRating,
      punctuality: options.punctuality,
      promotion: options.promotion,
      would_collab_again: options.wouldCollabAgain,
      feedback_text: options.feedbackText,
    }),
  });
};

export const submitGuestRating = (options: {
  applicationId: number;
  guestId: number;
  communicationRating?: number;
  organizationRating?: number;
  wouldCollabAgain?: 'yes' | 'maybe' | 'no';
  feedbackText?: string;
}): Promise<any> => {
  const functionName = 'submitGuestRating';
  return fetchAPI(`/api/collab-performance/guest-rate?guest_id=${options.guestId}`, functionName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      application_id: options.applicationId,
      communication_rating: options.communicationRating,
      organization_rating: options.organizationRating,
      would_collab_again: options.wouldCollabAgain,
      feedback_text: options.feedbackText,
    }),
  });
};

export const updateCollabMetrics = (
  performanceId: number,
  metrics: { views?: number; likes?: number; comments?: number; shares?: number }
): Promise<any> => {
  const functionName = 'updateCollabMetrics';
  return fetchAPI(`/api/collab-performance/${performanceId}/metrics`, functionName, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(metrics),
  });
};

export const getGuestStats = (userEmail: string): Promise<any> => {
  const functionName = 'getGuestStats';
  return fetchAPI(`/api/collab-performance/guest-stats?user_email=${encodeURIComponent(userEmail)}`, functionName);
};

export const getPublicGuestStats = (username: string): Promise<any> => {
  const functionName = 'getPublicGuestStats';
  return fetchAPI(
    `/api/collab-performance/public-guest-stats?username=${encodeURIComponent(username)}`,
    functionName,
  );
};

export const getCreatorCollabStats = (creatorId: number): Promise<any> => {
  const functionName = 'getCreatorCollabStats';
  return fetchAPI(`/api/collab-performance/creator-stats?creator_id=${creatorId}`, functionName);
};

export const getPendingCompletion = (creatorId: number): Promise<any> => {
  const functionName = 'getPendingCompletion';
  return fetchAPI(`/api/collab-performance/pending-completion?creator_id=${creatorId}`, functionName);
};

export const getCollabAttribution = (performanceId: number): Promise<any> => {
  const functionName = 'getCollabAttribution';
  return fetchAPI(`/api/collab-performance/${performanceId}/attribution`, functionName);
};

export const getAttributionSummary = (userEmail: string): Promise<any> => {
  const functionName = 'getAttributionSummary';
  return fetchAPI(`/api/collab-performance/attribution/summary?user_email=${encodeURIComponent(userEmail)}`, functionName);
};

export const trackVideoClick = (performanceId: number): Promise<any> => {
  const functionName = 'trackVideoClick';
  return fetchAPI(`/api/collab-performance/${performanceId}/track-click`, functionName, {
    method: 'POST',
    headers: {
      'X-Session-ID': SESSION_ID,
    },
  });
};

// ============================================================================
// NOTIFICATION PREFERENCES
// ============================================================================

export interface NotificationPreferences {
  application_updates: boolean;
  new_spot_alerts: boolean;
  booking_confirmations: boolean;
  messages: boolean;
  weekly_digest: boolean;
  email_enabled: boolean;
}

export const getNotificationPreferences = async (userId: number): Promise<NotificationPreferences> => {
  const response = await fetch(`${API_URL}/api/notifications/preferences/${userId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to load notification preferences');
  }
  return response.json();
};

export const updateNotificationPreferences = async (
  userId: number,
  preferences: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> => {
  const response = await fetch(`${API_URL}/api/notifications/preferences/${userId}`, {
    method: 'PATCH',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  });
  if (!response.ok) throw new Error('Failed to update notification preferences');
  return response.json();
};

// ==================== CONTENT DETECTION ====================

export const getDetectedContent = async (params: {
  creatorId?: number;
  platform?: string;
  status?: string;
  limit?: number;
}): Promise<any[]> => {
  const query = new URLSearchParams();
  if (params.creatorId) query.set('creator_id', String(params.creatorId));
  if (params.platform) query.set('platform', params.platform);
  if (params.status) query.set('status', params.status);
  if (params.limit) query.set('limit', String(params.limit));

  const response = await fetch(`${API_URL}/api/detected-content?${query.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch detected content');
  return response.json();
};

export const getDetectedContentStats = async (creatorId?: number): Promise<any> => {
  const query = creatorId ? `?creator_id=${creatorId}` : '';
  const response = await fetch(`${API_URL}/api/detected-content/stats${query}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch detection stats');
  return response.json();
};

export const confirmContentMatch = async (contentId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/api/detected-content/${contentId}/confirm-match`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to confirm match');
  return response.json();
};

export const assignContentMatch = async (contentId: number, applicationId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/api/detected-content/${contentId}/assign`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ application_id: applicationId }),
  });
  if (!response.ok) throw new Error('Failed to assign match');
  return response.json();
};

export const ignoreDetectedContent = async (contentId: number): Promise<any> => {
  const response = await fetch(`${API_URL}/api/detected-content/${contentId}/ignore`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to ignore content');
  return response.json();
};

// === Instagram Detection (Phase 4) ===

export const getInstagramConnectionStatus = async (creatorId?: number): Promise<any> => {
  const params = creatorId ? `?creator_id=${creatorId}` : '';
  const response = await fetch(`${API_URL}/api/detected-content/instagram/status${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to get Instagram status');
  return response.json();
};

export const triggerInstagramPoll = async (): Promise<any> => {
  const response = await fetch(`${API_URL}/api/detected-content/instagram/poll`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to trigger Instagram poll');
  return response.json();
};

export const triggerInstagramTokenRefresh = async (creatorId?: number): Promise<any> => {
  const params = creatorId ? `?creator_id=${creatorId}` : '';
  const response = await fetch(`${API_URL}/api/detected-content/instagram/token-refresh${params}`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to refresh Instagram token');
  return response.json();
};

// ─── Social Connect (OAuth) ─────────────────────────────────────

export const getConnectedAccounts = async (): Promise<any> => {
  const response = await fetch(`${API_URL}/api/social/connected-accounts`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to fetch connected accounts');
  return response.json();
};

export const initiateInstagramConnect = async (): Promise<{ auth_url: string }> => {
  const response = await fetch(`${API_URL}/api/social/connect/instagram`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to initiate Instagram connect');
  return response.json();
};

export const initiateYouTubeConnect = async (): Promise<{ auth_url: string }> => {
  const response = await fetch(`${API_URL}/api/social/connect/youtube`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Failed to initiate YouTube connect');
  return response.json();
};

export const disconnectSocialAccount = async (platform: string): Promise<any> => {
  const response = await fetch(`${API_URL}/api/social/disconnect/${platform}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to disconnect ${platform}`);
  return response.json();
};
